import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
	Button,
	Checkbox,
	CheckboxProps,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	TextField,
	withStyles
} from "@material-ui/core";
import DateFnsUtils from "@date-io/date-fns";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import ClearIcon from "@material-ui/icons/Clear";
import EventIcon from "@material-ui/icons/Event";
import { green } from "@material-ui/core/colors";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { UIContext } from "../../../../../contexts";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { Providers } from "../../../../../@types/providers.types";
import { Validation } from "../../../../../@types/validation.types";

import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { zipToCoordinateConverter } from "../../../../../utils/commonFunctions/zipToCoordinateConverter";
import { api } from "../../../../../utils/api";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { useNavigate } from "react-router-dom";

const date = new Date();
const maxDateRange = date.setDate(date.getDate());

const GreenCheckbox = withStyles({
	root: {
		color: green[400],
		"&$checked": {
			color: green[600]
		}
	},
	checked: {}
})((props: CheckboxProps) => <Checkbox color="default" {...props} />);

const AddProviders: React.FC = (): JSX.Element => {
	const [providerDetailsForm, setProviderDetailsForm] = useState<DynamicForm>();
	const [providersDetails, setProvidersDetails] = useState<Providers>({
		provider_type: "",
		provider_status: "",
		tax_id: "",
		first_name: "",
		middle_name: "",
		last_name: "",
		degree: "",
		contracted_through: "",
		facility_name: "",
		speciality: "",
		address: "",
		city: "",
		state: "",
		ZIP: "",
		latitude: "",
		longitude: "",
		phone_number: "",
		fax_number: "",
		effective_date: null,
		termination_date: null,
		fee_schedule: "",
		office_name: ""
	});
	const [pasted, setPasted] = useState<boolean>(false);
	const [validation, setValidation] = useState<Validation>({
		providersDetails: {},
		status: "invalid"
	});

	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const { setDashboardHeader } = useContext(UIContext);
	const _validation = useRef<Validation>();
	const provider_status_options = ["ACTIVE", "TERM"];
	const provider_type_options = ["DIRECT CONTRACT", "BSA"];
	const queryGroupNumber = useRouteParam("gn");
	const navigate = useNavigate();

	const handlePaste = (event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>) => {
		const { name } = event.target as HTMLInputElement;
		setPasted(true);
		setTimeout(() => null, 2000);
	};

	const handleProviderInputChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { name, value } = event.target;
			if (name === "tax_id") {
				if (!value || value.length == 0 || /^\d+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value.slice(0, 9) }));
				} else {
					if (value.length <= 9) alert("only number is allowed");
				}
				if (value.length >= 10 && providersDetails.tax_id.length < 9) {
					alert("Please enter a valid Tax-id (9 Digits)");
				}
			}

			if (name === "ZIP") {
				if (!value || value.length == 0 || /^\d+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value.slice(0, 5) }));

					const generatedLatitudeLongitude = zipToCoordinateConverter(value.slice(0, 5));
					const _latitude = String(generatedLatitudeLongitude[0]);
					const _longitude = String(generatedLatitudeLongitude[1]);

					if (_latitude && _longitude) {
						setProvidersDetails((providersDetails: Providers) => ({
							...providersDetails,
							[name]: value,
							latitude: _latitude.toString(),
							longitude: _longitude.toString()
						}));
					} else {
						if (!value || value.length <= 5) {
							setProvidersDetails((providersDetails: Providers) => ({
								...providersDetails,
								[name]: value
							}));
						} else {
							alert("ZIP character limit should be 5 digits");
						}
					}
				} else {
					alert("only number is allowed");
				}
			}

			if (name === "first_name") {
				if (!value || value.length == 0 || /^[a-zA-Z]+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value }));
				} else {
					alert("only character is allowed");
				}
			}

			if (name === "middle_name") {
				if (!value || value.length == 0 || /^[a-zA-Z]+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value }));
				} else {
					alert("only character is allowed");
				}
			}

			if (name === "last_name") {
				if (!value || value.length == 0 || /^[a-zA-Z]+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value }));
				} else {
					alert("only character is allowed");
				}
			}

			if (name === "phone_number") {
				if (!value || value.length == 0 || /^\d+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value.slice(0, 10) }));
				} else {
					alert("only number is allowed");
				}
				if (value.length >= 11) {
					alert("Please enter a valid Phone-no. (10 Digits)");
				}
			}

			if (name === "fee_schedule") {
				if (!value || value.length == 0 || /^\d+$/.test(value)) {
					setProvidersDetails(Object.assign({}, providersDetails, { [name]: value }));
				} else {
					alert("only number is allowed");
				}
			}

			if (
				name != "ZIP" &&
				name != "tax_id" &&
				name != "first_name" &&
				name != "middle_name" &&
				name != "last_name" &&
				name != "phone_number" &&
				name != "fee_schedule"
			) {
				setProvidersDetails(Object.assign({}, providersDetails, { [name]: value }));
			}
		},
		[providersDetails]
	);

	const handleValidation = useCallback(async () => {
		const {
			tax_id,
			provider_status,
			provider_type,
			first_name,
			middle_name,
			last_name,
			degree,
			contracted_through,
			facility_name,
			// speciality,
			ZIP,
			// address,
			// city,
			// state,
			// phone_number,
			// fax_number,
			// effective_date,
			// termination_date,
			// fee_schedule,
			// office_name
			...restValidationField
		} = providersDetails;

		_validation.current = {
			...validation,
			providersDetails: {},
			status: "invalid"
		};

		let flag = true;

		if (tax_id.length < 9) {
			_validation.current.providersDetails["tax_id"] = "Tax Id Character limit should be 9 digits";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (ZIP.length === 0) {
			_validation.current.providersDetails["ZIP"] = "ZIP is required. Character Limit is of 5 digits";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (first_name.length === 0) {
			_validation.current.providersDetails["first_name"] = "First Name is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (last_name.length === 0) {
			_validation.current.providersDetails["last_name"] = "Last Name is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (provider_type.length === 0) {
			_validation.current.providersDetails["provider_type"] = "Providers Type is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (provider_status.length === 0) {
			_validation.current.providersDetails["provider_status"] = "Providers Status is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (degree.length === 0) {
			_validation.current.providersDetails["degree"] = "Degree is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (contracted_through.length === 0) {
			_validation.current.providersDetails["contracted_through"] = "Contracted Through is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		if (facility_name.length === 0) {
			_validation.current.providersDetails["facility_name"] = "Facility Name is required!";
			_validation.current.status = "invalid";
			flag = false;
		}

		// For rest of the fields
		Object.entries(restValidationField).forEach(([fieldLabel, fieldValues]) => {
			if (_validation.current) {
				if (fieldValues === null || fieldValues.length === 0) {
					_validation.current.providersDetails[fieldLabel] = `${fieldLabel} Field is Required`;
					_validation.current.status = "invalid";
					flag = false;
				}
			}
		});

		if (flag === true) _validation.current.status = "valid";
		else _validation.current.status = "invalid";

		setValidation(Object.assign({}, _validation.current));
		return _validation.current["status"];
	}, [providersDetails, validation]);

	const handleDateInputChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			if (name === "effective_date")
				setProvidersDetails(Object.assign({}, providersDetails, { [name]: parsedDate }));
			if (name === "termination_date")
				setProvidersDetails(Object.assign({}, providersDetails, { [name]: parsedDate }));
		},
		[providersDetails]
	);

	const handleClearDateField = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, name: string) => {
			setProvidersDetails(Object.assign({}, providersDetails, { [name]: null }));
		},
		[providersDetails]
	);

	const handleAddProvidersSubmitClick = useCallback(async () => {
		try {
			const validationStatus = await handleValidation();
			if (validationStatus === "valid") {
				const newProviderResponse = await api.providers.addProviders(
					providersDetails,
					queryGroupNumber as string
				);

				if (newProviderResponse) {
					alert("Successfully Added new Provider!");
					navigate(`/employer/providers?gn=${queryGroupNumber}`);
				}
			} else {
				throw new Error("Please check the information");
			}
		} catch (error) {
			alert(error);
		}
	}, [handleValidation, navigate, providersDetails, queryGroupNumber]);

	useEffect(() => {
		setProviderDetailsForm(
			Object.assign(
				{},
				{
					"Add Provider Form": [
						{
							label: "Provider Type",
							name: "provider_type",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.provider_type,
							type: "select",
							placeholder: "Select the provider type"
						},
						{
							label: "Provider Status",
							name: "provider_status",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.provider_status,
							type: "select",
							placeholder: "Select the provider status"
						},
						{
							label: "Tax Id",
							name: "tax_id",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.tax_id,
							type: "textfield",
							placeholder: "Enter the Tax-Id"
						},
						{
							label: "First Name",
							name: "first_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.first_name,
							type: "textfield",
							placeholder: "Enter the first Name"
						},
						{
							label: "Middle Name",
							name: "middle_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.middle_name,
							type: "textfield",
							placeholder: "Enter the Middle Name"
						},
						{
							label: "Last Name",
							name: "last_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.last_name,
							type: "textfield",
							placeholder: "Enter the Last Name"
						},
						{
							label: "Degree",
							name: "degree",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.degree,
							type: "textfield",
							placeholder: "Enter the Degree"
						},
						{
							label: "Contracted Through",
							name: "contracted_through",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.contracted_through,
							type: "textfield",
							placeholder: "Enter Contracted Through"
						},
						{
							label: "Facility Name",
							name: "facility_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.facility_name,
							type: "textfield",
							placeholder: "Enter the Facility Name"
						},
						{
							label: "Speciality",
							name: "speciality",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.speciality,
							type: "textfield",
							placeholder: "Enter Specility"
						},
						{
							label: "Address",
							name: "address",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.address,
							type: "textfield",
							placeholder: "Enter the Address"
						},
						{
							label: "City",
							name: "city",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.city,
							type: "textfield",
							placeholder: "Enter Contracted Through"
						},
						{
							label: "State",
							name: "state",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.state,
							type: "textfield",
							placeholder: "Enter the State Name"
						},
						{
							label: "ZIP",
							name: "ZIP",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.ZIP,
							type: "textfield",
							placeholder: "Enter the ZIP Code"
						},
						// {
						// 	label: "Latitude",
						// 	name: "latitude",
						// 	onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
						// 	value: providersDetails.latitude,
						// 	type: "textfield",
						// 	placeholder: "Enter the Latitude"
						// },
						// {
						// 	label: "Longitude",
						// 	name: "longitude",
						// 	onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
						// 	value: providersDetails.longitude,
						// 	type: "textfield",
						// 	placeholder: "Enter the Longitude"
						// },
						{
							label: "Phone Number",
							name: "phone_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.phone_number,
							type: "textfield",
							placeholder: "Enter the Phone Number"
						},
						{
							label: "Fax Number",
							name: "fax_number",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.fax_number,
							type: "textfield",
							placeholder: "Enter the Fax Number"
						},
						{
							label: "Effective Date",
							name: "effective_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateInputChange(date, name),
							value: providersDetails.effective_date,
							type: "date",
							placeholder: "Enter the Effective Date"
						},
						{
							label: "Termination Date",
							name: "termination_date",
							onChange: (date: MaterialUiPickersDate, name: string) => handleDateInputChange(date, name),
							value: providersDetails.termination_date,
							type: "date",
							placeholder: "Enter the Termination Date"
						},
						{
							label: "Fee Schedule (in Percentage %)",
							name: "fee_schedule",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.fee_schedule,
							type: "textfield",
							placeholder: "Enter the Fee Schedule"
						},
						{
							label: "Office Name",
							name: "office_name",
							onChange: (event: React.ChangeEvent<HTMLInputElement>) => handleProviderInputChange(event),
							value: providersDetails.office_name,
							type: "textfield",
							placeholder: "Enter the Office Name"
						}
					]
				}
			)
		);
	}, [handleDateInputChange, handleProviderInputChange, providersDetails]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.add_providers);
	}, [setDashboardHeader]);

	return (
		<div className="add-providers-main-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Button
					className="button-green"
					onClick={handleAddProvidersSubmitClick}
					variant="contained"
					style={{
						pointerEvents: "unset"
					}}
				>
					<span className="button-label-with-icon" style={{ width: 120, padding: 2 }}>
						Add Providers
					</span>
					<span>
						<PersonAddIcon className="button-icon" />
					</span>
				</Button>
			</div>

			<Grid container spacing={1} className="pf-grid-container">
				{providerDetailsForm &&
					Object.entries(providerDetailsForm).map(([key, value], index) => {
						return (
							<div key={index}>
								<div className="pf-title">{key}</div>
								<Suspense fallback={<div />}>
									<Paper className="pf-paper-container" elevation={3} variant="outlined">
										{value.map((field, index) => {
											return (
												<Grid item key={index} xl={12} lg={12} md={12} sm={12} xs={12}>
													<Grid container spacing={1} className="pf-label" style={{}}>
														<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
															<div className="pf-label-text" id="pf-label-text">
																{field.label}
															</div>
														</Grid>
														<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
															{field.type === "textfield" ? (
																<TextField
																	className="text-field"
																	id={"input-text-field"}
																	type="string"
																	name={field.name}
																	placeholder={field.placeholder}
																	value={field.value}
																	onChange={field.onChange}
																	onPaste={(
																		event: React.ClipboardEvent<HTMLDivElement>
																	) => handlePaste(event)}
																	variant="outlined"
																	helperText={validation.providersDetails[field.name]}
																	style={{
																		pointerEvents: "unset",
																		width: "340px",
																		borderRadius: 50
																	}}
																/>
															) : field.type === "select" ? (
																<>
																	<Select
																		id="text-align-options"
																		name={field.name}
																		onChange={field.onChange}
																		style={{
																			pointerEvents: "unset",
																			minWidth: 180,
																			textAlign: "left",
																			overflowY: "visible"
																		}}
																		MenuProps={{
																			style: { zIndex: 35960 }
																		}}
																		inputProps={{ "aria-label": "Without label" }}
																	>
																		<MenuItem value="" disabled>
																			Select Value
																		</MenuItem>
																		{field.name === "provider_status"
																			? provider_status_options.map(
																					(option, index) => {
																						return (
																							<MenuItem
																								key={index}
																								value={option}
																							>
																								{option}
																							</MenuItem>
																						);
																					}
																			  )
																			: field.name === "provider_type"
																			? provider_type_options.map(
																					(option, index) => {
																						return (
																							<MenuItem
																								key={index}
																								value={option}
																							>
																								{option}
																							</MenuItem>
																						);
																					}
																			  )
																			: null}
																	</Select>
																	{validation.providersDetails[field.name] ? (
																		<div className="details">
																			<span className="select-validation-text2">
																				{
																					validation.providersDetails[
																						field.name
																					]
																				}
																			</span>
																		</div>
																	) : null}
																</>
															) : field.type === "date" ? (
																<>
																	<MuiPickersUtilsProvider utils={DateFnsUtils}>
																		<KeyboardDatePicker
																			className="from-date-input"
																			id="from-date-input"
																			inputVariant="outlined"
																			label={field.label}
																			placeholder={field.placeholder}
																			name={field.name}
																			format="MM/dd/yyyy"
																			error={false}
																			value={field.value}
																			maxDate={maxDateRange}
																			readOnly={false}
																			onChange={(date: MaterialUiPickersDate) =>
																				field.onChange(date, field.name)
																			}
																			InputProps={{
																				startAdornment: (
																					<IconButton
																						onClick={(
																							event: React.MouseEvent<
																								HTMLButtonElement,
																								MouseEvent
																							>
																						) =>
																							handleClearDateField(
																								event,
																								field.name
																							)
																						}
																						style={{ order: 1 }}
																					>
																						<ClearIcon
																							color="disabled"
																							fontSize="small"
																						/>
																					</IconButton>
																				),
																				readOnly: true
																			}}
																			InputLabelProps={{
																				shrink: true
																			}}
																			//style={{ width: "340px", borderRadius: 50 }}
																			keyboardIcon={
																				<EventIcon
																					style={{
																						color: "#7cb342"
																					}}
																				/>
																			}
																			KeyboardButtonProps={{
																				"aria-label": "change date"
																			}}
																			style={{
																				// cursor: !isButtonSelected ? "not-allowed" : "pointer",
																				pointerEvents: "unset",
																				width: "340px",
																				borderRadius: 50
																			}}
																		/>
																	</MuiPickersUtilsProvider>
																	{validation.providersDetails[field.name] ? (
																		<div className="details">
																			<span className="select-validation-text2">
																				{
																					validation.providersDetails[
																						field.name
																					]
																				}
																			</span>
																		</div>
																	) : null}
																</>
															) : field.type === "checkbox" ? (
																<>
																	<div className="check-support" id="check-support">
																		<span>
																			<GreenCheckbox
																				onChange={field.onChange}
																				name={field.name}
																				checked={field.value}
																			/>
																		</span>
																		<span
																			className="pf-label-text"
																			id="pf-label-text"
																		>
																			{field.label}
																		</span>
																	</div>
																</>
															) : null}
														</Grid>
													</Grid>
												</Grid>
											);
										})}
									</Paper>
								</Suspense>
							</div>
						);
					})}
			</Grid>

			<div className="pf-action-button-container margin-top-action-button" id="pf-action-button-container">
				<Button
					className="button-green"
					onClick={handleAddProvidersSubmitClick}
					variant="contained"
					style={{
						pointerEvents: "unset"
					}}
				>
					<span className="button-label-with-icon" style={{ width: 120, padding: 2 }}>
						Add Providers
					</span>
					<span>
						<PersonAddIcon className="button-icon" />
					</span>
				</Button>
			</div>
		</div>
	);
};

export default AddProviders;
